/**
 * ZAXIA Frontend Types
 * Type definitions mirroring backend Hotel Lab data
 */

// =============================================================================
// ENUMS
// =============================================================================

export enum ServiceCategory {
  HOUSEKEEPING = 'HOUSEKEEPING',
  SPA = 'SPA',
  RESTAURANT = 'RESTAURANT',
  BAR = 'BAR',
  ROOM_SERVICE = 'ROOM_SERVICE',
  HOTEL_INFO = 'HOTEL_INFO',
  ACTIVITY = 'ACTIVITY',
  TRANSPORT = 'TRANSPORT',
}

export enum TaskStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export enum RoomStatus {
  VACANT = 'VACANT',
  RESERVED = 'RESERVED',
  OCCUPIED = 'OCCUPIED',
  CLEANING = 'CLEANING',
  OUT_OF_SERVICE = 'OUT_OF_SERVICE',
}

export enum RoomType {
  STANDARD = 'STANDARD',
  DELUXE = 'DELUXE',
  SUITE = 'SUITE',
}

export enum StayStatus {
  RESERVED = 'RESERVED',
  CHECKED_IN = 'CHECKED_IN',
  CHECKED_OUT = 'CHECKED_OUT',
  CANCELLED = 'CANCELLED',
}

export enum CalendarEventStatus {
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
}

// =============================================================================
// NAVIGATION
// =============================================================================

export type NavigationZone = 'sidebar' | 'main' | 'tv';

export interface ZoneState {
  activeZone: NavigationZone;
  previousZone: NavigationZone;
}

// =============================================================================
// HOTEL
// =============================================================================

export interface OperatingHours {
  open: string;
  close: string;
}

export interface Hotel {
  hotelId: string;
  name: string;
  timezone: string;
  currency: string;
  defaultLanguage: string;
  languages: string[];
  operatingHours: {
    spa: OperatingHours;
    restaurant: {
      breakfast: OperatingHours;
      lunch: OperatingHours;
      dinner: OperatingHours;
    };
  };
  policies: {
    lateCheckoutFee: number;
    lateCheckoutMaxHour: string;
    minibarPosting: boolean;
  };
}

// =============================================================================
// ROOM
// =============================================================================

export interface Room {
  roomId: string;
  hotelId: string;
  roomNumber: string;
  roomType: RoomType;
  floor: number;
  status: RoomStatus;
  tvId: string | null;
  currentStayId: string | null;
}

// =============================================================================
// GUEST
// =============================================================================

export interface GuestPreferences {
  spaInterest: boolean;
  diningInterest: boolean;
  sustainabilityOptIn: boolean;
}

export interface Guest {
  guestId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  language: string;
  preferences: GuestPreferences | null;
}

// =============================================================================
// STAY
// =============================================================================

export interface GreenPreferences {
  greenStayOptIn: boolean;
  skipCleaningDates: string[];
  towelsOnlyDates: string[];
}

export interface Stay {
  stayId: string;
  hotelId: string;
  roomId: string;
  guestId: string;
  checkInDate: string;
  checkOutDate: string;
  actualCheckIn: string | null;
  actualCheckOut: string | null;
  status: StayStatus;
  greenPreferences: GreenPreferences;
}

// =============================================================================
// SERVICE
// =============================================================================

export interface ServiceCatalogItem {
  serviceId: string;
  hotelId: string;
  category: ServiceCategory;
  name: string;
  description: string;
  durationMinutes: number;
  price: number | null;
  capacity: number;
  available: boolean;
}

// =============================================================================
// CALENDAR EVENT
// =============================================================================

export interface CalendarEvent {
  eventId: string;
  stayId: string;
  serviceType: ServiceCategory;
  title: string;
  datetime: string;
  durationMinutes: number;
  location: string | null;
  sourceBookingId: string | null;
  sourceTaskId: string | null;
  status: CalendarEventStatus;
}

// =============================================================================
// TASK
// =============================================================================

export interface Task {
  taskId: string;
  hotelId: string;
  roomId: string;
  stayId: string;
  type: string;
  serviceId: string | null;
  status: TaskStatus;
  priority: string;
  assignedTo: string | null;
  notes: string | null;
}

// =============================================================================
// TV CHANNEL
// =============================================================================

export interface TVChannel {
  id: string;
  name: string;
  number: number;
  currentProgram: string;
  logo?: string;
}

// =============================================================================
// SERVICE CARD
// =============================================================================

export interface ServiceCardData {
  id: string;
  title: string;
  category: ServiceCategory;
  icon: string;
  image: string;
  description: string;
}

// =============================================================================
// WEATHER
// =============================================================================

export interface WeatherData {
  temperature: number;
  condition: 'sunny' | 'cloudy' | 'rainy' | 'partly-cloudy';
  humidity: number;
  location: string;
}

// =============================================================================
// MESSAGE
// =============================================================================

export interface Message {
  id: string;
  from?: string;
  title: string;
  content: string;
  timestamp: string;
  read: boolean;
  type: 'info' | 'alert' | 'welcome';
}

// =============================================================================
// BOOKING (Phase 2B)
// =============================================================================

export enum BookingStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN_PROGRESS',
  ON_HOLD = 'ON_HOLD',
}

export interface RoomServiceItem {
  itemId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  notes?: string;
}

export interface Booking {
  bookingId: string;
  stayId: string;
  serviceId: string;
  serviceName: string;
  category: ServiceCategory;
  datetime: string;
  durationMinutes: number;
  status: BookingStatus;
  price: number | null;
  location?: string;
  notes?: string;
  items?: RoomServiceItem[];
  createdAt: string;
}

// =============================================================================
// HOTEL BILL (Phase 2B)
// =============================================================================

export enum ChargeCategory {
  ROOM = 'ROOM',
  MINIBAR = 'MINIBAR',
  ROOM_SERVICE = 'ROOM_SERVICE',
  SPA = 'SPA',
  RESTAURANT = 'RESTAURANT',
  BAR = 'BAR',
  ACTIVITY = 'ACTIVITY',
  TRANSPORT = 'TRANSPORT',
  OTHER = 'OTHER',
}

export interface BillCharge {
  chargeId: string;
  stayId: string;
  category: ChargeCategory;
  description: string;
  notes?: string;
  amount: number;
  datetime: string;
  bookingId?: string;
  items?: RoomServiceItem[];
}

export interface HotelBill {
  stayId: string;
  guestName: string;
  roomNumber: string;
  checkInDate: string;
  checkOutDate: string;
  charges: BillCharge[];
  totalAmount: number;
  currency: string;
}

// =============================================================================
// SERVICE DETAIL (Phase 2B)
// =============================================================================

export interface ServiceOption {
  optionId: string;
  name: string;
  description: string;
  price: number | null;
  durationMinutes?: number;
  available: boolean;
  /** Fixed session buttons (e.g. pool) — overrides service-level sessionSlots for this option. */
  sessionSlots?: SessionSlot[];
}

/** A fixed session slot (Full Day / Morning / Afternoon) used by pool bookings. */
export interface SessionSlot {
  id: string;
  label: string;          // 'Full Day' | 'Morning' | 'Afternoon'
  icon: string;           // emoji
  time: string;           // start time stored in booking, e.g. '07:00' or '13:00'
  durationLabel: string;  // human display, e.g. '07:00 – 21:00'
  durationMinutes: number;
  price: number | null;   // null = complimentary
}

export interface ServiceDetail {
  id: string;
  title: string;
  category: ServiceCategory;
  icon: string;
  images: string[];
  description: string;
  longDescription: string;
  location?: string;
  operatingHours?: string;
  options: ServiceOption[];
  /** When set, replaces the free-form time picker with fixed session buttons. */
  sessionSlots?: SessionSlot[];
}
